import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Sheet,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/joy';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Link as RouterLink } from 'react-router-dom';

import MainContent from '../common/MainContent';
import PageHero from '../common/PageHero';
import {
  fetchAdminUser,
  updateAdminUser,
  updateAdminUserRoles,
  type AdminUser,
} from '../../api';

type FeedbackState = { type: 'success' | 'danger'; message: string } | null;

export default function AdminUserProfile({ userId }: { userId: number | string }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [spiritualName, setSpiritualName] = useState('');
  const [roles, setRoles] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchAdminUser(userId)
      .then((data) => {
        if (!mounted) return;
        if (data) {
          setUser(data);
          setDisplayName(data.displayName ?? '');
          setSpiritualName(data.spiritualName ?? '');
          setRoles((data.roles ?? []).join(', '));
        } else {
          setUser(null);
          setDisplayName('');
          setSpiritualName('');
          setRoles('');
        }
      })
      .catch(() => {
        if (!mounted) return;
        setUser(null);
        setFeedback({
          type: 'danger',
          message: 'We were unable to load this devotee profile. Please refresh and try again.',
        });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!feedback) return;
    const timeout = setTimeout(() => setFeedback(null), 3500);
    return () => clearTimeout(timeout);
  }, [feedback]);

  const parsedRoles = useMemo(
    () =>
      roles
        .split(',')
        .map((role) => role.trim())
        .filter(Boolean),
    [roles],
  );

  const initialRoles = useMemo(() => user?.roles ?? [], [user]);

  const hasChanges =
    !!user &&
    (displayName.trim() !== (user.displayName ?? '').trim() ||
      spiritualName.trim() !== (user.spiritualName ?? '').trim() ||
      JSON.stringify(parsedRoles) !== JSON.stringify(initialRoles));

  const resetForm = () => {
    if (!user) return;
    setDisplayName(user.displayName ?? '');
    setSpiritualName(user.spiritualName ?? '');
    setRoles((user.roles ?? []).join(', '));
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await updateAdminUser(user.id, {
        displayName: displayName.trim(),
        spiritualName: spiritualName.trim(),
      });
      await updateAdminUserRoles(user.id, parsedRoles);
      const updatedUser: AdminUser = {
        ...user,
        displayName: displayName.trim(),
        spiritualName: spiritualName.trim(),
        roles: parsedRoles,
      };
      setUser(updatedUser);
      setFeedback({
        type: 'success',
        message: 'Profile updated successfully. May their seva continue to blossom!',
      });
    } catch (error) {
      setFeedback({
        type: 'danger',
        message: 'There was a problem saving the profile. Please try again shortly.',
      });
    } finally {
      setSaving(false);
    }
  };

  const heroTitle = user
    ? `Guiding ${user.displayName}'s seva journey`
    : 'Stewarding devotee profile';

  const heroDescription = user
    ? `Keep ${user.displayName}'s details current so that communications and seva invitations remain heartfelt and personal.`
    : 'Keep devotee records up to date so every communication carries warmth and accuracy.';

  const createdDate = user?.createdAt ? new Date(user.createdAt) : null;

  return (
    <MainContent>
      <PageHero
        eyebrow="Devotee management"
        title={heroTitle}
        description={heroDescription}
        actions={
          <>
            <Button
              component={RouterLink}
              to={`/users/${userId}/donations`}
              variant="soft"
              color="neutral"
              startDecorator={<VolunteerActivismRoundedIcon />}
            >
              View offerings
            </Button>
            <Button
              component={RouterLink}
              to={`/users/${userId}/spiritual`}
              variant="outlined"
              color="neutral"
              startDecorator={<FavoriteRoundedIcon />}
            >
              Spiritual profile
            </Button>
          </>
        }
      />

      <Grid container spacing={3} alignItems="stretch">
        <Grid xs={12} lg={8}>
          <Card variant="outlined" sx={{ borderRadius: 'xl', p: { xs: 2.5, md: 3 }, height: '100%' }}>
            <Stack spacing={2.5} height="100%">
              <Stack spacing={0.5}>
                <Typography level="title-lg" fontWeight={700}>
                  Profile details
                </Typography>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Update names and roles so this devotee feels seen in every interaction.
                </Typography>
              </Stack>

              {loading ? (
                <Sheet
                  variant="soft"
                  sx={{
                    borderRadius: 'lg',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <CircularProgress color="primary" size="md" aria-label="Fetching devotee profile" />
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Fetching devotee profile…
                  </Typography>
                </Sheet>
              ) : !user ? (
                <Sheet
                  variant="soft"
                  color="danger"
                  sx={{ borderRadius: 'lg', p: 3, textAlign: 'center', gap: 1, display: 'grid' }}
                >
                  <Typography level="title-sm" fontWeight={600}>
                    We couldn’t find this devotee.
                  </Typography>
                  <Typography level="body-sm">
                    The record may have been removed. Please return to the devotees list and choose another profile.
                  </Typography>
                </Sheet>
              ) : (
                <Stack spacing={2.5} flex={1}>
                  <FormControl>
                    <FormLabel>Display name</FormLabel>
                    <Input
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                      aria-label="display name"
                      size="md"
                    />
                    <FormHelperText>The name we’ll share on dashboards and communications.</FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Spiritual name</FormLabel>
                    <Input
                      value={spiritualName}
                      onChange={(event) => setSpiritualName(event.target.value)}
                      aria-label="spiritual name"
                      size="md"
                    />
                    <FormHelperText>Optional — captured for blessings and personalised seva invites.</FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Roles</FormLabel>
                    <Input
                      value={roles}
                      onChange={(event) => setRoles(event.target.value)}
                      aria-label="roles"
                      size="md"
                      placeholder="Admin, Seva lead, Volunteer"
                    />
                    <FormHelperText>Separate multiple roles with commas to help teams identify their service areas.</FormHelperText>
                  </FormControl>

                  <Stack spacing={1}>
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                      Preview of assigned roles
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {parsedRoles.length ? (
                        parsedRoles.map((role) => (
                          <Chip key={role} variant="soft" color="primary">
                            {role}
                          </Chip>
                        ))
                      ) : (
                        <Chip variant="soft" color="neutral">
                          No roles assigned yet
                        </Chip>
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} pt={0.5}>
                    <Button
                      onClick={handleSave}
                      data-testid="save-user"
                      loading={saving}
                      disabled={!hasChanges || saving}
                    >
                      Save changes
                    </Button>
                    <Button
                      variant="outlined"
                      color="neutral"
                      onClick={resetForm}
                      startDecorator={<RestartAltRoundedIcon />}
                      disabled={!hasChanges || saving}
                    >
                      Reset
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Card>
        </Grid>

        <Grid xs={12} lg={4}>
          <Stack spacing={3} height="100%">
            <Card variant="outlined" sx={{ borderRadius: 'xl', p: { xs: 2.5, md: 3 } }}>
              <Stack spacing={1.5}>
                <Typography
                  level="title-sm"
                  textTransform="uppercase"
                  letterSpacing={0.8}
                  sx={{ color: 'text.tertiary' }}
                >
                  Account snapshot
                </Typography>
                {loading ? (
                  <Stack spacing={1}>
                    <Skeleton variant="text" level="body-md" width="80%" />
                    <Skeleton variant="text" level="body-sm" width="60%" />
                    <Skeleton variant="rectangular" height={28} sx={{ borderRadius: 'sm' }} />
                  </Stack>
                ) : user ? (
                  <Stack spacing={1.25}>
                    <Stack spacing={0.25}>
                      <Typography
                        level="body-xs"
                        textTransform="uppercase"
                        letterSpacing={0.6}
                        sx={{ color: 'text.tertiary' }}
                      >
                        Email
                      </Typography>
                      <Typography level="body-md" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {user.email}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.25}>
                      <Typography
                        level="body-xs"
                        textTransform="uppercase"
                        letterSpacing={0.6}
                        sx={{ color: 'text.tertiary' }}
                      >
                        Joined community
                      </Typography>
                      <Typography level="body-md">
                        {createdDate ? createdDate.toLocaleDateString() : '—'}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5}>
                      <Typography
                        level="body-xs"
                        textTransform="uppercase"
                        letterSpacing={0.6}
                        sx={{ color: 'text.tertiary' }}
                      >
                        Current roles
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(user.roles ?? []).length ? (
                          (user.roles ?? []).map((role) => (
                            <Chip key={role} size="sm" variant="soft" color="primary">
                              {role}
                            </Chip>
                          ))
                        ) : (
                          <Chip size="sm" variant="soft" color="neutral">
                            None assigned
                          </Chip>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                ) : (
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Profile details are unavailable for this devotee.
                  </Typography>
                )}
              </Stack>
            </Card>

            <Card
              variant="soft"
              color="primary"
              sx={{
                borderRadius: 'xl',
                p: { xs: 2.5, md: 3 },
                boxShadow: '0 16px 32px rgba(13,109,219,0.18)',
              }}
            >
              <Stack spacing={1.25}>
                <Typography level="title-md" fontWeight={700}>
                  Keep seva records radiant
                </Typography>
                <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                  Every accurate detail ensures announcements, donation receipts, and seva invitations reach the right devotee with warmth.
                </Typography>
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        variant="soft"
        color={feedback?.type === 'danger' ? 'danger' : 'success'}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setFeedback(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          color={feedback?.type === 'danger' ? 'danger' : 'success'}
          variant="soft"
          startDecorator={feedback?.type === 'danger' ? <ErrorOutlineRoundedIcon /> : <CheckCircleRoundedIcon />}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>
    </MainContent>
  );
}
